import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DropdownOption } from '@storybook/components/dropdown/dropdown.component';
import { CurrencySign } from '@pdqdev/pdq-shared/dist/entities';
import { ShopService } from '@app/services/shops/shop.service';
import {
  DEFAULT_FIXED_RATE,
  DEFAULT_PERCENTAGE_RATE,
  OrderProtectionExtension,
  OrderProtectionFormGroup,
} from '../../model';

const FIXED_LABEL = 'Protection rate';
const PERCENTAGE_LABEL = 'Protection rate (% of subtotal)';
const FIXED_STEP = 0.01;
const FIXED_PRECISION = 2;
const PERCENTAGE_STEP = 0.1;
const PERCENTAGE_PRECISION = 1;

@Component({
  selector: 'order-protection-pricing',
  templateUrl: './order-protection-pricing.component.html',
  styleUrls: ['./order-protection-pricing.component.scss'],
  standalone: false,
})
export class OrderProtectionPricingComponent implements OnInit, OnChanges {
  @Input() formGroup: FormGroup<OrderProtectionFormGroup>;
  @Input() productMinPrice: number | null = null;
  @Input() productMaxPrice: number | null = null;

  readonly pricingTypeOptions: DropdownOption[] = [
    { label: 'Fixed', value: OrderProtectionExtension.RateType.FIXED },
    { label: 'Percentage', value: OrderProtectionExtension.RateType.PERCENTAGE },
  ];
  selectedPricingTypeOption: DropdownOption;
  rateLabel: string;
  rateSymbol: CurrencySign | '%';
  rateStep: number;
  ratePrecision: number;
  rateMin = 0;
  rateMax: number | undefined;
  rateHint: string | null = null;

  constructor(private shopService: ShopService) {}

  ngOnInit() {
    const rateType = this.formGroup.controls.rateType.value;
    this.selectedPricingTypeOption = this.resolveTypeOption(rateType);
    this.applyRateType(rateType);
  }

  ngOnChanges() {
    if (this.formGroup) {
      this.applyRateType(this.formGroup.controls.rateType.value);
    }
  }

  onPricingTypeChange(option: DropdownOption) {
    const rateType = option.value as OrderProtectionExtension.RateType;
    const isPercentage = rateType === OrderProtectionExtension.RateType.PERCENTAGE;
    this.formGroup.controls.rateType.setValue(rateType);
    this.formGroup.controls.rate.setValue(isPercentage ? DEFAULT_PERCENTAGE_RATE : DEFAULT_FIXED_RATE);
    this.selectedPricingTypeOption = option;
    this.applyRateType(rateType);
  }

  private resolveTypeOption(rateType: OrderProtectionExtension.RateType): DropdownOption {
    return this.pricingTypeOptions.find((option) => option.value === rateType) ?? this.pricingTypeOptions[0];
  }

  private applyRateType(rateType: OrderProtectionExtension.RateType) {
    const isPercentage = rateType === OrderProtectionExtension.RateType.PERCENTAGE;
    this.rateLabel = isPercentage ? PERCENTAGE_LABEL : FIXED_LABEL;
    this.rateSymbol = isPercentage ? '%' : ((this.shopService.shopCurrencySymbol ?? '$') as CurrencySign);
    this.rateStep = isPercentage ? PERCENTAGE_STEP : FIXED_STEP;
    this.ratePrecision = isPercentage ? PERCENTAGE_PRECISION : FIXED_PRECISION;

    const min = this.productMinPrice;
    const max = this.productMaxPrice;
    if (!isPercentage && min != null && max != null) {
      this.rateMin = min;
      this.rateMax = max;
      this.rateHint = `Between ${this.rateSymbol}${min.toFixed(2)} and ${this.rateSymbol}${max.toFixed(2)}`;
    } else {
      this.rateMin = 0;
      this.rateMax = undefined;
      this.rateHint = null;
    }
  }
}
